import { Inject } from '@odinjs/odin';
import { FetchAPI, FieldAPI, FieldListAPI, InputAPI, ModalService } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnBeforeFetch, OnChange, OnFocus, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { isEmptyArrayOrObject, isUndefined } from '@philips-internal/oswald-ui-utils';
import { PredefinedValidators } from '../../../src/utils/predefined-validators';
import { FragmentNames, PseudonymAppointmentsStep_2Aliases } from '../../codegen';
import { defaultIfUndefined } from '../../utils/object-utils';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { Company, ProcedureModel, ProcedureModelChange, SimpleObjectResponse } from './pseudonym-interfaces';
import { AppointmentsIcons, AppointmentsSteps, ScheduleType, YES } from './pseudonym-types';
import { HttpService, MediaType, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { Value } from '@philips-internal/oswald-ui-core';
import { ModalButtonType } from '@philips-internal/oswald-ui-constants';
import { COMPANY_VISIBILITY, EXAM_SIDE, GENDER_VISIBILITY, MEDICAL_NUMBER_VISIBILITY, PROFESSIONAL_VISIBILITY } from './pseudonym-enums';
import PseudonymUtils from './pseudonym-utils';

@Controller({ component: PseudonymAppointmentsStep_2Aliases.LAYOUT_SCHEDULING_ITEMS, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_STEP_2 })
export default class PseudonymAppointmentsStep2Controller extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsStep_2Aliases.FETCH_EXAM_SIDE)
    fetchExamAPI!: FetchAPI;

  @Inject(PseudonymAppointmentsStep_2Aliases.FIELD_LIST_PROCEDURES)
    fieldProceduresAPI!: FieldListAPI;

  @Inject(PseudonymAppointmentsStep_2Aliases.FETCH_PROCEDURES)
    fetchProceduresAPI!: FetchAPI;

  @Inject(PseudonymAppointmentsStep_2Aliases.FETCH_COMPANY)
    fetchCompanyAPI!: FetchAPI;

  @Inject(PseudonymAppointmentsStep_2Aliases.FIELD_COMPANY)
    fieldCompanyAPI!: FieldAPI;

  @Inject(PseudonymAppointmentsStep_2Aliases.FIELD_PROFESSIONAL)
    fieldProfessionalAPI!: FieldAPI;

  private loopAvoider = false;
  private lastPhysician: Value[] = [];

  @OnMount({ target: PseudonymAppointmentsStep_2Aliases.LAYOUT_SCHEDULING_ITEMS })
  handleMountScheduleItems(): void {
    PseudonymAppointmentModel.stepTwo.clearFields = () => this.clearFields();
    PseudonymAppointmentModel.stepTwo.clearProfessionalField = () => this.clearProfessionalField();
  }

  clearFields(): void {
    this.fieldProceduresAPI.setValue([{}]);
  }

  clearProfessionalField(): void {
    const procedures = this.fieldProceduresAPI.getValue() as unknown as Array<ProcedureModelChange>;
    const procedureField = this.fieldProfessionalAPI.getValue() as Record<string, Value>;
    if (procedures.length == 1
      && Object.keys(procedures[0]).length > 0
      && procedureField?.professionalCode
      && procedures[0].procedures.professional != procedureField?.professionalCode) {
      this.clearProfessional(this.fieldProfessionalAPI);
    }
  }

  getQuantityOfProcedures(): number {
    const procedures = this.fieldProceduresAPI.getValue() as unknown as Array<ProcedureModelChange>;
    return procedures.length;
  }

  setbuttonAddFieldVisibility(procedureList: ProcedureModelChange[]): void {
    const visibility = procedureList.some((procedure: ProcedureModelChange) => ScheduleType.CHECKUP === procedure.procedures?.typeProcedureCode);
    this.fieldProceduresAPI.setShowAddField(!visibility);
  }

  @OnChange({ target: PseudonymAppointmentsStep_2Aliases.FIELD_LIST_PROCEDURES })
  onChangeSchedulingItemsField(event: ChangeEvent<ProcedureModelChange[]>): void {
    if (!event) {
      return;
    }
    if (this.context.get('escondeuGenero')) {
      this.context.set({escondeuGenero: false});
      return;
    }
    if (this.context.get('escondeuProfissional')) {
      this.context.set({escondeuProfissional: false});
      return;
    }

    const fieldValue = event.value;
    const fieldOldValue = event.oldValue;

    //se apagar um registro nao executa o change, as vezes buga e nao remove os campos da tela
    if (fieldOldValue && fieldOldValue.length > fieldValue?.length) {
      const allItensOk = fieldValue.every(field => {
        if (field.procedures.genderMandatory) {
          if (!field.procedures.gender) {
            return false;
          }
        }
        if (field.procedures.professionalMandatory) {
          if (!field.procedures.professional) {
            return false;
          }
        }
        return true;
      });
      this.setWizardStepDone(AppointmentsSteps.STEP_2_SCHEDULING_ITEMS, allItensOk);
      PseudonymAppointmentModel.stepTwo.stepTwoOk = allItensOk;
      return;
    }

    this.setbuttonAddFieldVisibility(fieldValue);

    const wasItemRemoved = fieldValue.length < (fieldOldValue?.length ?? 0);

    const finishedItems: boolean[] = [];

    const fieldListProcedures = event.target as unknown as FieldListAPI;
    const rowApis = fieldListProcedures.getRowsAPIs();
    rowApis.forEach(async (row, index) => {
      const isLastItem = rowApis.length - 1 === index;

      const fieldProceduresApi = row.get('field-procedures') as FieldAPI;
      const procedure = fieldProceduresApi.getValue() as ProcedureModel;
      this.hideInitialSteps(procedure?.sequence === undefined);

      const fieldExamSideApi = row.get('field-exam-side') as FieldAPI;
      this.showSideOptions(procedure, fieldExamSideApi);

      const fieldCompanyApi = row.get(PseudonymAppointmentsStep_2Aliases.FIELD_COMPANY) as FieldAPI;
      this.setCompanyFieldVisibility(procedure?.viewCompanyCheckupField, fieldCompanyApi);

      const fieldStepListApi = row.get(PseudonymAppointmentsStep_2Aliases.FIELD_STEP_LIST) as FieldAPI;
      PseudonymUtils.setStepCheckupListVisibility(fieldStepListApi, procedure?.viewCheckupStepData);

      const fieldPhysicianLicenseNumberApi = row.get(PseudonymAppointmentsStep_2Aliases.FIELD_CRM) as FieldAPI;
      const fieldStateApi = row.get(PseudonymAppointmentsStep_2Aliases.FIELD_STATE) as FieldAPI;
      const fieldDoctorNameApi = row.get(PseudonymAppointmentsStep_2Aliases.FIELD_DOCTOR_NAME) as FieldAPI;
      const fieldGenderApi = row.get(PseudonymAppointmentsStep_2Aliases.FIELD_GENDER) as FieldAPI;
      const fieldProfessionalApi = row.get(PseudonymAppointmentsStep_2Aliases.FIELD_PROFESSIONAL) as FieldAPI;
      this.setMedicalLicenseNumberFieldVisibility(procedure?.viewMedicalLicenseNumberField, fieldPhysicianLicenseNumberApi, fieldStateApi, fieldDoctorNameApi);
      this.setGenderFieldVisibility(procedure, fieldGenderApi, procedure?.viewGenderField);
      const genderValue = fieldGenderApi.getValue() as Record<string, string>;
      this.setProfessionalFieldVisibility(fieldProfessionalApi, procedure, genderValue?.genderType);
      if (fieldOldValue) {
        const changeItem = fieldValue[index]?.procedures?.sequence != fieldOldValue[index]?.procedures?.sequence;
        if (changeItem) {
          this.clearGender(fieldGenderApi);
          this.clearProfessional(fieldProfessionalApi);
          fieldValue[index].gender = null;
          fieldValue[index].professional = null;
        } else if (fieldValue[index]?.gender?.genderType != fieldOldValue[index]?.gender?.genderType) {
          this.clearProfessional(fieldProfessionalApi);
          fieldValue[index].professional = null;
          if (!fieldValue[index]?.gender) {
            fieldValue[index].gender = null;
          }
        }
      }
      this.handleStateFieldValidator(procedure?.viewMedicalLicenseNumberField, fieldPhysicianLicenseNumberApi, fieldStateApi);
      await this.findPhysicianName(fieldPhysicianLicenseNumberApi, fieldStateApi, fieldDoctorNameApi);
      if (fieldOldValue?.[index]?.procedures?.sequence !== fieldValue?.[index]?.procedures?.sequence && !wasItemRemoved) {
        this.validateSideField(event);
        this.handleCheckupStep();
        this.handleStepTwo(procedure);
        this.clearMedicalLicenseInfo(fieldPhysicianLicenseNumberApi, fieldStateApi, fieldDoctorNameApi);
      }
      if (isLastItem && this.context.get('copyMedicalLicenseInfo')) {
        this.copyMedicalLicenseInfo(fieldPhysicianLicenseNumberApi, fieldStateApi, fieldDoctorNameApi);
      } else if (this.shouldUpdateLastPhysician([fieldPhysicianLicenseNumberApi, fieldStateApi, fieldDoctorNameApi])) {
        this.setLastPhysician(fieldPhysicianLicenseNumberApi, fieldStateApi, fieldDoctorNameApi);
      }

      finishedItems[index] =  this.validateMandatoryFields([fieldPhysicianLicenseNumberApi, fieldStateApi, fieldDoctorNameApi, fieldCompanyApi, fieldGenderApi, fieldProfessionalApi]) &&
                              this.validateSideField(event) &&
                              this.validateCheckupField(procedure, event);
      const allItensOk = finishedItems.every(item => item);
      this.setWizardStepDone(AppointmentsSteps.STEP_2_SCHEDULING_ITEMS, allItensOk);
      PseudonymAppointmentModel.stepTwo.stepTwoOk = allItensOk;
    });
    this.loopAvoider = true;
  }

  private handleStepTwo(procedure?: ProcedureModel): void {
    if (PseudonymAppointmentModel.stepTwo.hasCheckup || procedure?.sequence === undefined) {
      return;
    }

    if (PseudonymAppointmentModel.stepTwo.hasExam) {
      this.setWizardShowStep(AppointmentsSteps.STEP_3_ADDITIONAL_INFORMATION);
    } else {
      this.setWizardHiddenStep(AppointmentsSteps.STEP_3_ADDITIONAL_INFORMATION);
    }

    this.showQuizStep();

    this.setWizardHiddenStep(AppointmentsSteps.STEP_6_SCHEDULE_PERIOD);

    if (PseudonymAppointmentModel.isQuizAvailable
      && PseudonymAppointmentModel.stepTwo.hasProcedureChange
      && !PseudonymAppointmentModel.stepTwo.hasCheckup
      && this.isWizardStepHidden(AppointmentsSteps.STEP_4_QUIZ)) {
      this.setWizardShowStep(AppointmentsSteps.STEP_4_QUIZ);
    }

    this.showLocationStep(PseudonymAppointmentModel.stepOne.healthInsurance.healthInsuranceId);
  }

  private showQuizStep(): void {
    const options = { headers: { accept: MediaType.TEXT_PLAIN } };

    HttpService.get<SimpleObjectResponse>(APPLICATION_ENDPOINTS.QUIZ_AVAILABLE, {}, options).then(response => {
      const isQuizAvailable = response.body?.value === YES;
      PseudonymAppointmentModel.isQuizAvailable = isQuizAvailable;

      if (!isQuizAvailable && !this.getIsRescheduleContext()) {
        this.setWizardHiddenStep(AppointmentsSteps.STEP_4_QUIZ);
      }
    });
  }

  @OnChange({target: [PseudonymAppointmentsStep_2Aliases.FIELD_CRM, PseudonymAppointmentsStep_2Aliases.FIELD_STATE]})
  setLoopAvoiderFlag(): void {
    this.loopAvoider = false;
  }

  setCompanyFieldVisibility(viewField: string = COMPANY_VISIBILITY.HIDE, fieldCompanyApi: FieldAPI): void {
    switch (viewField) {
      case COMPANY_VISIBILITY.MANDATORY:
        PseudonymUtils.setFieldsConfig([fieldCompanyApi], true, true);
        break;
      case COMPANY_VISIBILITY.SHOW:
        PseudonymUtils.setFieldsConfig([fieldCompanyApi], true, false);
        break;
      case COMPANY_VISIBILITY.HIDE:
      default:
        PseudonymUtils.setFieldsConfig([fieldCompanyApi], false, false);
        break;
    }
  }

  @OnMount({ target: PseudonymAppointmentsStep_2Aliases.FETCH_COMPANY })
  onMountFetchCompany(event: MountEvent): void {
    const fetchApi = event.target as FetchAPI;
    this.fieldCompanyAPI.setValue([]);
    fetchApi.setParameters({ 'checkupType': PseudonymAppointmentModel.stepCheckupSuggestions.checkupType });
  }

  setMedicalLicenseNumberFieldVisibility(viewField: string = MEDICAL_NUMBER_VISIBILITY.NOT_SHOW, numberField: FieldAPI, stateField: FieldAPI, nameField: FieldAPI): void {
    switch (viewField) {
      case MEDICAL_NUMBER_VISIBILITY.MANDATORY:
        PseudonymUtils.setFieldsConfig([numberField, stateField, nameField], true, true);
        break;
      case MEDICAL_NUMBER_VISIBILITY.SHOW:
        PseudonymUtils.setFieldsConfig([numberField, stateField, nameField], true, false);
        break;
      case MEDICAL_NUMBER_VISIBILITY.NOT_SHOW:
      default:
        PseudonymUtils.setFieldsConfig([numberField, stateField, nameField], false, false);
        break;
    }
  }

  setGenderFieldVisibility(procedure: ProcedureModel, genderField: FieldAPI, viewField: string = GENDER_VISIBILITY.HIDE): void {
    let isMandatory;
    switch (viewField) {
      case GENDER_VISIBILITY.MANDATORY:
        PseudonymUtils.setFieldsConfig([genderField], true, true);
        isMandatory = true;
        break;
      case GENDER_VISIBILITY.SHOW:
        PseudonymUtils.setFieldsConfig([genderField], true, false);
        isMandatory = false;
        break;
      case GENDER_VISIBILITY.HIDE:
      default:
        this.clearGender(genderField);
        PseudonymUtils.setFieldsConfig([genderField], false, false);
        isMandatory = false;
        break;
    }
    if (procedure) {
      procedure.genderMandatory = isMandatory;
    }
    // esse validate eh para quando o campo sai de obrigatorio, as vezes nao removia o vermelho do campo
    genderField.validate();
  }

  setProfessionalFieldVisibility(professionalField: FieldAPI, procedure :ProcedureModel, genderValue :string): void {
    let isMandatory;
    const viewField = procedure?.viewProfessionalField ?? PROFESSIONAL_VISIBILITY.HIDE;
    switch (viewField) {
      case PROFESSIONAL_VISIBILITY.MANDATORY:
        PseudonymUtils.setFieldsConfig([professionalField], true, true);
        this.setProfessionalFetchParameters(professionalField, procedure, genderValue);
        isMandatory = true;
        break;
      case PROFESSIONAL_VISIBILITY.SHOW:
        PseudonymUtils.setFieldsConfig([professionalField], true, false);
        this.setProfessionalFetchParameters(professionalField, procedure, genderValue);
        isMandatory = false;
        break;
      case PROFESSIONAL_VISIBILITY.HIDE:
      default:
        this.clearProfessional(professionalField);
        PseudonymUtils.setFieldsConfig([professionalField], false, false);
        isMandatory = false;
        break;
    }
    if (procedure) {
      procedure.professionalMandatory = isMandatory;
    }
    // esse validate eh para quando o campo sai de obrigatorio, as vezes nao removia o vermelho do campo
    professionalField.validate();
  }

  setProfessionalFetchParameters(professionalField: FieldAPI, procedure :ProcedureModel, genderValue :string): void {
    if (professionalField.getChildren().size == 0) {
      return;
    }
    const professinalFetch = professionalField.getChildren().get(PseudonymAppointmentsStep_2Aliases.FETCH_PROFESSIONAL) as FetchAPI;
    const healthInsurance = PseudonymAppointmentModel.stepOne.healthInsurance;

    professinalFetch.setParameters({
      cdCategoria: defaultIfUndefined(healthInsurance.categoryId, ''),
      cdConvenio: defaultIfUndefined(healthInsurance.healthInsuranceId.toString(), ''),
      cdEspecialidade: defaultIfUndefined(procedure?.specialtySequence, 0),
      cdPessoaFisica: String(PseudonymAppointmentModel.stepOne.currentPatientSelected.id),
      cdPlano: defaultIfUndefined(healthInsurance.planId, ''),
      establishmentFilter: defaultIfUndefined(PseudonymAppointmentModel.stepOne.appointmentEstablishment?.cdEstablishment, ''),
      genderValue: genderValue || 'A',
      nrSeqProcInterno: defaultIfUndefined(procedure?.internalProcedureSequence, 0),
      typeProcedureCode: defaultIfUndefined(procedure?.typeProcedureCode, '')
    });
  }

  clearGender(genderField :FieldAPI): void {
    if (!genderField.getValue()) {
      return;
    }
    this.context.set({escondeuGenero: true});
    genderField.setValue(null);
  }

  clearProfessional(fieldProfessionalApi :FieldAPI): void {
    if (!fieldProfessionalApi.getValue()) {
      return;
    }
    this.context.set({escondeuProfissional: true});
    fieldProfessionalApi.setValue(null);
  }

  shouldUpdateLastPhysician(fields: FieldAPI[]): boolean {
    return !fields.some(field => field.getValue() === '');
  }

  copyMedicalLicenseInfo(numberField: FieldAPI, stateField: FieldAPI, nameField: FieldAPI): void {
    numberField.setValue(this.lastPhysician[0]);
    stateField.setValue(this.lastPhysician[1]);
    nameField.setValue(this.lastPhysician[2]);
    this.context.set({copyMedicalLicenseInfo: false});
  }

  clearMedicalLicenseInfo(numberField: FieldAPI, stateField: FieldAPI, nameField: FieldAPI): void {
    numberField.setValue('');
    stateField.setValue('');
    nameField.setValue('');
  }

  setLastPhysician(numberField: FieldAPI, stateField: FieldAPI, nameField: FieldAPI): void {
    this.lastPhysician[0] = numberField.getValue();
    this.lastPhysician[1] = stateField.getValue();
    this.lastPhysician[2] = nameField.getValue();
  }

  @OnMount({ target: PseudonymAppointmentsStep_2Aliases.FIELD_EXAM_SIDE })
  onMountFieldExamSide(): void {
    const specificSide = this.context.get('side') as string;
    if (specificSide) {
      this.fetchExamAPI.setParameters({ side: specificSide });
      this.fetchExamAPI.forceFetch();
    }
  }

  @OnFocus({ target: PseudonymAppointmentsStep_2Aliases.AUTOCOMPLETE_PROCEDURES })
  onFocusFieldProcedures(): void {
    this.fetchProceduresAPI.setParameters({ personCode: PseudonymAppointmentModel.stepOne.currentPatientSelected.id });
    this.fetchProceduresAPI.forceFetch();
  }

  @OnAfterFetch({ target: PseudonymAppointmentsStep_2Aliases.FETCH_PROCEDURES })
  onAfterFetchProcedures(event: FetchEvent): void {
    if (!event?.data) {
      return;
    }

    const procedureModelList = event.data as Array<ProcedureModel>;
    procedureModelList.forEach(procedureModel => {
      this.procedureImage(procedureModel);
      procedureModel.procedureDescription = this.mountProcedureDescription(procedureModel);
      procedureModel.information = this.mountProcedureInformation(procedureModel);
    });
  }

  @OnChange({target: PseudonymAppointmentsStep_2Aliases.FIELD_PROCEDURES})
  setCopyMedicalLicenseInfoFlag(event: ChangeEvent<ProcedureModel>): void {
    const data = event.value;

    if (ScheduleType.CHECKUP === data.typeProcedureCode) {
      PseudonymAppointmentModel.clearStepSeven();
      if (this.getQuantityOfProcedures() > 1) {
        ModalService.openDialog({
          closeable: true,
          dialogDescription: MessageService.translate('patient.checkup.only.one'),
          dockButtons: [{
            action: () => { this.checkupRuleControl(); },
            title: 'patient.button.confirm',
            type: ModalButtonType.SUCCESS,
          }]
        });
      }
      this.context.set({
        checkupCode: data.checkupTypeSequence,
        checkupType: ScheduleType.CHECKUP_TYPE,
        personCode: PseudonymAppointmentModel.stepOne.currentPatientSelected.id
      });

      PseudonymAppointmentModel.stepCheckupSuggestions.checkupType = data.checkupTypeSequence;
      if (Object.keys(this.fetchCompanyAPI).length > 0) {
        this.fieldCompanyAPI.setValue([]);
        this.fetchCompanyAPI.setParameters({ 'checkupType': PseudonymAppointmentModel.stepCheckupSuggestions.checkupType });
        this.fetchCompanyAPI.forceFetch();
      }
    }

    if (data.viewMedicalLicenseNumberField === MEDICAL_NUMBER_VISIBILITY.NOT_SHOW ||
      data.typeProcedureCode !== ScheduleType.EXAM ||
      this.lastPhysician.length === 0 ||
      this.lastPhysician.some(item => isUndefined(item) || item == '') ||
      this.context.get('copyMedicalLicenseInfo')) {
      return;
    }

    ModalService.openDialog({
      closeable: true,
      dialogDescription: MessageService.translate('patient.physician.copy'),
      dockButtons: [{
        action: () => { this.context.set({copyMedicalLicenseInfo: false}); },
        title: 'patient.cancel',
        type: ModalButtonType.CANCEL,
      },
      {
        action: () => {
          this.context.set({copyMedicalLicenseInfo: true});
          const fieldProcedures = event.target as FieldAPI;
          fieldProcedures.setValue(data);
        },
        title: 'patient.button.confirm',
        type: ModalButtonType.SUCCESS,
      }]
    });
  }

  private checkupRuleControl(): void {
    this.setWizardStepDone(AppointmentsSteps.STEP_2_SCHEDULING_ITEMS, false);
  }

  private mountProcedureDescription(procedure: ProcedureModel): string {
    let description = procedure.procedureDescription;

    if (!description) {
      if (procedure.internalProcedureDescription) {
        return procedure.internalProcedureDescription;
      }

      if (procedure.typeProcedureCode === ScheduleType.CONSULTATION) {
        if (procedure.physicianName) {
          description = defaultIfUndefined(procedure.physicianName, '');
        }
        if (procedure.specialtyDescription) {
          description = description ? description + ' | ' : '';
          description = description + defaultIfUndefined(procedure.specialtyDescription, '');
        }
      } else if (ScheduleType.CHECKUP === procedure.typeProcedureCode) {
        description = procedure.checkupTypeDescription;
      }
    }

    return description;
  }

  private mountProcedureInformation(procedure: ProcedureModel): string {
    let information = this.addInformation('', procedure.typeProcedureDescription);
    information = this.addInformation(information, procedure.specialtyDescription);
    information = this.addInformation(information, procedure.physicianName);
    return information;
  }

  private addInformation(information: string, value: string): string {
    const checkedValue = defaultIfUndefined(value, '');
    return information && checkedValue ? information.concat(' | ', checkedValue) : information.concat(checkedValue);
  }

  private showSideOptions(procedure: ProcedureModel, fieldExamSideApi: FieldAPI): void {
    if (procedure?.typeProcedureCode !== ScheduleType.EXAM || !procedure?.specifiedSide) {
      this.setConfigurationFieldExamSide(fieldExamSideApi, false, false, EXAM_SIDE.NOT_SHOW);
      return;
    }

    switch (procedure.specifiedSide) {
      case EXAM_SIDE.NOT_BOTH:
        this.setConfigurationFieldExamSide(fieldExamSideApi, true, true, EXAM_SIDE.NOT_BOTH);
        break;
      case EXAM_SIDE.REQUIRED:
        this.setConfigurationFieldExamSide(fieldExamSideApi, true, true, EXAM_SIDE.REQUIRED);
        break;
      case EXAM_SIDE.NOT_REQUIRED:
        this.setConfigurationFieldExamSide(fieldExamSideApi, true, false, EXAM_SIDE.NOT_REQUIRED);
        break;
      default:
        this.setConfigurationFieldExamSide(fieldExamSideApi, false, false, EXAM_SIDE.NOT_SHOW);
        break;
    }
  }

  private setConfigurationFieldExamSide(fieldExamSideApi: FieldAPI, visible: boolean, required: boolean, examSide: EXAM_SIDE): void {
    const requiredValues = PredefinedValidators.required();
    fieldExamSideApi.setVisible(visible);
    fieldExamSideApi.setValidators(required ? [requiredValues] : []);
    this.context.set({ side: examSide });
  }

  private updateSchedulingModel(procedureModelChangeList: Array<ProcedureModelChange>): void {
    const procedures = [] as Array<ProcedureModel>;
    let hasExam = false;
    let hasCheckup = false;
    let isOnlyConsultation = true;

    procedureModelChangeList.forEach(procedureModelChange => {
      if (procedureModelChange.procedures) {
        if (procedureModelChange.gender?.genderType) {
          procedureModelChange.procedures.gender = procedureModelChange.gender.genderType;
        } else {
          procedureModelChange.procedures.gender = '';
        }
        if (procedureModelChange.professional?.professionalCode) {
          procedureModelChange.procedures.professional = procedureModelChange.professional.professionalCode;
        } else {
          procedureModelChange.procedures.professional = '';
        }
        procedureModelChange.procedures.examSide = procedureModelChange.examSide;
        procedureModelChange.procedures.externalPhysicianInfo = {
          externalPhysicianLicenseNumber: procedureModelChange.externalPhysicianLicenseNumber,
          externalPhysicianLicenseState: procedureModelChange.externalPhysicianLicenseState?.vlDominio,
          externalPhysicianName: procedureModelChange.externalPhysicianName
        };
        procedures.push(procedureModelChange.procedures);

        if (procedureModelChange.procedures.typeProcedureCode !== ScheduleType.CONSULTATION) {
          isOnlyConsultation = false;
        }
        if (procedureModelChange.procedures.typeProcedureCode === ScheduleType.EXAM) {
          hasExam = true;
        }
        if (procedureModelChange.procedures.typeProcedureCode === ScheduleType.CHECKUP) {
          hasCheckup = true;
        }
      }
    });

    PseudonymAppointmentModel.stepTwo.hasExam = hasExam;
    PseudonymAppointmentModel.stepTwo.hasCheckup = hasCheckup;
    PseudonymAppointmentModel.stepTwo.hasProcedureChange = true;
    PseudonymAppointmentModel.stepTwo.isOnlyConsultation = isOnlyConsultation;
    PseudonymAppointmentModel.stepTwo.procedures = procedures;
  }

  private async findPhysicianName(numberField: FieldAPI, stateField: FieldAPI, nameField: FieldAPI): Promise<void> {
    const stateValue = stateField.getValue() as Record<string, Value>;
    const state = stateValue?.vlDominio;
    const nameFieldInput = Array.from(nameField.getChildren()).flat()[1] as InputAPI;

    if (numberField.getValue() && state && !this.loopAvoider) {
      await HttpService.get(APPLICATION_ENDPOINTS.GET_PHYSICIAN_NAME_BY_MD_CODE, {
        medicalLicenseCode: numberField.getValue() as string,
        medicalLicenseState: state as string
      }).then(physicianData => {
        const { nmPessoaFisica } = physicianData.body as Record<string, Value>;
        nameField.setValue(nmPessoaFisica);
        nameFieldInput.setEnabled(false);
      }).catch(() => {
        nameFieldInput.setEnabled(true);
      });
    }
  }

  private validateSideField(event: ChangeEvent<ProcedureModelChange[]>): boolean {
    if (!Array.isArray(event.value)) {
      return false;
    }

    const procedureModelChange = event.value;
    this.updateSchedulingModel(procedureModelChange);

    const requiredSides = [EXAM_SIDE.NOT_BOTH, EXAM_SIDE.REQUIRED] as Array<string>;
    return procedureModelChange.every(item => {
      const procedure = item.procedures;
      return !isEmptyArrayOrObject(procedure) && (!requiredSides.includes(procedure?.specifiedSide) || procedure?.examSide);
    });
  }

  private validateMandatoryFields(fields: FieldAPI[]): boolean {
    return fields.every(field => {
      if (field.getValidators().find(value => value.name == PredefinedValidators.required().name) && field.isVisible()) {
        const value = field.getValue();
        return !!value;
      }
      return true;
    });
  }

  private handleStateFieldValidator(viewField: string = MEDICAL_NUMBER_VISIBILITY.NOT_SHOW, numberField: FieldAPI, stateField: FieldAPI): void {
    if (viewField === MEDICAL_NUMBER_VISIBILITY.MANDATORY) {
      return;
    }

    if (numberField.getValue()) {
      stateField.setValidators([PredefinedValidators.required()]);
    } else {
      stateField.removeValidator('required');
    }
  }

  @OnBeforeFetch({ target: PseudonymAppointmentsStep_2Aliases.FETCH_PROCEDURES })
  onBeforeFetchProcedures(event: FetchEvent): void {
    const api = event.target as FetchAPI;
    api.setParameters({
      establishmentCode: PseudonymAppointmentModel.stepOne.appointmentEstablishment?.cdEstablishment || 0,
      personCode: PseudonymAppointmentModel.stepOne.currentPatientSelected.id
    });
  }

  private validateCheckupField(procedureField: ProcedureModel, event: ChangeEvent<ProcedureModelChange[]>): boolean {
    if (ScheduleType.CHECKUP === procedureField.typeProcedureCode) {
      return event.value.length === 1;
    }
    return true;
  }

  @OnChange([PseudonymAppointmentsStep_2Aliases.FIELD_COMPANY])
  onChangeCompanyField(event: ChangeEvent<Value>): void {
    const company = event.value as Company;
    PseudonymAppointmentModel.referenceCompanyCode = company?.companyCode;
  }

  private procedureImage(procedureModel: ProcedureModel): void {
    switch (procedureModel.typeProcedureCode) {
      case ScheduleType.CONSULTATION:
        this.existsImage(procedureModel, AppointmentsIcons.CONSULTATION);
        break;
      case ScheduleType.EXAM:
        this.existsImage(procedureModel, AppointmentsIcons.EXAM);
        break;
      case ScheduleType.SERVICE:
        this.existsImage(procedureModel, AppointmentsIcons.SERVICE);
        break;
      case ScheduleType.CHECKUP:
        this.existsImage(procedureModel, AppointmentsIcons.CHECKUP);
        break;
      default:
        procedureModel.image = '';
        break;
    }
  }

  private existsImage(procedureModel: ProcedureModel, icon: AppointmentsIcons): void {
    if (procedureModel.physicianImage64) {
      procedureModel.image = procedureModel.physicianImage64;
    } else {
      procedureModel.image = icon;
    }
  }
}
